import PrivacyTipIcon from '@mui/icons-material/PrivacyTip'
import { Avatar, Box, Container, Typography } from '@mui/material'

import { Wrapper, AppBarR as PersistentDrawerRight } from '../components/common'

const PrivacyPolicy = () => {
  return (
    <>
      <PersistentDrawerRight></PersistentDrawerRight>
      <Wrapper>
        <Container component='main' maxWidth='md'>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              py: 4
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <PrivacyTipIcon fontSize='small' />
              </Avatar>
              <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
                Privacy Policy
              </Typography>
            </Box>

            <Typography
              variant='body2'
              sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}
            >
              Last Updated: March 19, 2026
            </Typography>

            <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.7 }}>
              [Your Company Name] ("we", "us", or "our") is committed to
              protecting the privacy of your personal information in accordance
              with the Privacy Act 1988 (Cth) and the Australian Privacy
              Principles (APPs).
            </Typography>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' }}
            >
              1. Information We Collect
            </Typography>
            <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.7 }}>
              We collect information necessary to provide the desk booking
              service, including:
            </Typography>
            <Box component='ul' sx={{ pl: 3, mb: 3 }}>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>Identity Data:</strong> Name and business email
                  address.
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>Booking Data:</strong> Office location, desk number,
                  and date/time of attendance.
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>Technical Data:</strong> IP addresses, browser types,
                  and usage logs (for security and performance).
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>Analytics Data:</strong> We use Google Analytics 4 to
                  collect anonymized usage data, including page views, user
                  interactions, and feature usage patterns. This data helps us
                  improve the service and understand how users interact with the
                  platform.
                </Typography>
              </li>
            </Box>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' }}
            >
              2. How We Use Your Data
            </Typography>
            <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.7 }}>
              We use the collected data to:
            </Typography>
            <Box component='ul' sx={{ pl: 3, mb: 3 }}>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Manage and confirm workspace bookings.
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Provide "Who's In" visibility to other members of your
                  specific organization.
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Maintain the security of the platform.
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Analyze usage patterns and improve the service through Google
                  Analytics 4.
                </Typography>
              </li>
            </Box>
            <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.7 }}>
              We <strong>do not</strong> sell your personal data to third
              parties or use it for marketing without explicit consent.
            </Typography>

            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600, mb: 2, mt: 2 }}
            >
              Google Analytics Data Collection
            </Typography>
            <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.7 }}>
              We use Google Analytics 4 to understand how users interact with
              our service. The analytics data we collect includes:
            </Typography>
            <Box component='ul' sx={{ pl: 3, mb: 2 }}>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>Page Views:</strong> Which pages you visit and how
                  long you spend on them.
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>User Actions:</strong> Interactions such as creating
                  bookings, managing offices, inviting team members, and other
                  feature usage (tracked anonymously).
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>Technical Information:</strong> Device type, browser,
                  operating system, and general location (country/city level).
                </Typography>
              </li>
            </Box>
            <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>What We Do NOT Collect in Analytics:</strong>
            </Typography>
            <Box component='ul' sx={{ pl: 3, mb: 2 }}>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Your email address
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Your name
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Office names
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Desk names or numbers
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  Any other personally identifiable information (PII)
                </Typography>
              </li>
            </Box>
            <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.7 }}>
              All analytics events use anonymized identifiers only. We use
              separate Google Analytics projects for staging and production
              environments to ensure data accuracy and security.
            </Typography>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' }}
            >
              3. Data Security & "Use at Your Own Risk"
            </Typography>
            <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.7 }}>
              We implement industry-standard security measures, including
              SSL/TLS encryption for data in transit and at rest. However, no
              method of transmission over the internet is 100% secure.{' '}
              <strong>
                While we take all reasonable steps to protect your data, use of
                the Service is at your own risk.
              </strong>
            </Typography>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' }}
            >
              4. Third-Party Service Providers
            </Typography>
            <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.7 }}>
              We may use third-party companies to facilitate our Service,
              including:
            </Typography>
            <Box component='ul' sx={{ pl: 3, mb: 2 }}>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>Cloud Hosting:</strong> Firebase/Google Cloud Platform
                  for application hosting and data storage.
                </Typography>
              </li>
              <li>
                <Typography variant='body1' sx={{ lineHeight: 1.7 }}>
                  <strong>Analytics:</strong> Google Analytics 4 for anonymized
                  usage analytics and service improvement.
                </Typography>
              </li>
            </Box>
            <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.7 }}>
              These third parties have access to your Personal Information only
              to perform these tasks on our behalf and are obligated not to
              disclose or use it for any other purpose. Google Analytics data is
              governed by{' '}
              <a
                href='https://policies.google.com/privacy'
                target='_blank'
                rel='noopener noreferrer'
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Google's Privacy Policy
              </a>{' '}
              and complies with GDPR requirements.
            </Typography>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' }}
            >
              5. International Transfers
            </Typography>
            <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.7 }}>
              Your information may be transferred to—and maintained on—computers
              located outside of Australia where data protection laws may
              differ. Your consent to this Privacy Policy represents your
              agreement to that transfer.
            </Typography>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' }}
            >
              6. Your Rights (Access and Correction)
            </Typography>
            <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.7 }}>
              You have the right to access, update, or delete the personal
              information we hold about you. You can typically do this within
              your account settings or by contacting us directly.
            </Typography>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' }}
            >
              7. Contact Us
            </Typography>
            <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.7 }}>
              If you have any questions about this Privacy Policy or wish to
              make a complaint regarding your privacy, please contact us at:{' '}
              <strong>[Your Email Address]</strong>.
            </Typography>
            <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.7 }}>
              If we cannot resolve your complaint, you may contact the{' '}
              <strong>
                Office of the Australian Information Commissioner (OAIC)
              </strong>
              .
            </Typography>
          </Box>
        </Container>
      </Wrapper>
    </>
  )
}

export default PrivacyPolicy
